// Lib
import { useEffect } from 'react';
import Handsontable from 'handsontable/base';

// Utils
import useDebouncedCallback from '../../../../utils/react/useDebouncedCallback';
import { repositionAutofillHandle } from '../utils/tableCellSelectionUtils';
import useThrottledCallback from '../../../../utils/react/useThrottledCallback';

// Types
import { CellSelections, TableContentData } from '../../../../../common/table/TableTypes';
import usePrevious from '../../../../utils/react/usePrevious';

interface UseTableStateSyncEffectsProps {
    mounted: boolean;
    tableContentData: TableContentData;
    currentCellSelections: CellSelections;
    tableContentColWidthsGU: number[];
    gridSize: number;
    inList: string | null;
    isUndoingOrRedoing: boolean;
    isGridMoving: boolean;

    hotTableContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    hotTableInstanceRef: React.MutableRefObject<Handsontable.Core | null>;
    tableStateSyncHandlers: {
        syncTableData: () => void;
        syncCellSelections: () => void;
        syncColWidths: () => void;
    };
}

/**
 * Custom hook for managing table state sync side effects
 * Handles syncing between Redux state and Handsontable instance
 *
 * @param props - Hook configuration
 */
export const useTableStateSyncEffects = (props: UseTableStateSyncEffectsProps): void => {
    const {
        tableContentData,
        currentCellSelections,
        tableContentColWidthsGU,
        gridSize,
        inList,
        isUndoingOrRedoing,
        isGridMoving,
        hotTableContainerRef,
        hotTableInstanceRef,

        tableStateSyncHandlers,
    } = props;

    const prevTableContentData = usePrevious(tableContentData);

    const { syncTableData, syncCellSelections, syncColWidths } = tableStateSyncHandlers;

    const debouncedSyncTableData = useDebouncedCallback(syncTableData, 100, [syncTableData]);
    const debouncedSyncCellSelections = useDebouncedCallback(syncCellSelections, 100, [syncCellSelections]);
    const throttledSyncColWidths = useThrottledCallback(syncColWidths, 100, [syncColWidths]);

    /**
     * Sync table data from Redux state if there are changes
     */
    useEffect(() => {
        const isInitialLoad = !prevTableContentData && tableContentData;

        if (isInitialLoad || isUndoingOrRedoing || isGridMoving) syncTableData();
        else debouncedSyncTableData();
    }, [tableContentData]);

    /**
     * Sync cell selections from Redux state if there are changes
     */
    useEffect(() => {
        if (isUndoingOrRedoing) syncCellSelections();
        else debouncedSyncCellSelections();
    }, [currentCellSelections]);

    /**
     * Sync column widths on container resize, e.g. when column is resized
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current || !inList) return;
        const resizeObserver = new ResizeObserver(() => throttledSyncColWidths());

        resizeObserver.observe(hotTableInstanceRef.current?.rootElement);

        return () => resizeObserver.disconnect();
    }, []);

    /**
     * Sync column widths from Redux state if there are changes
     */
    useEffect(() => {
        const colResizeInstance = hotTableInstanceRef?.current?.getPlugin<'manualColumnResize'>('manualColumnResize');

        // We don't want to update hot table if we are still resizing
        // this is because if we are resizing multiple columns, this function can be called before all the colWidthGu
        // values have been accurately updated in the afterColumnResize function
        if ((colResizeInstance as any)?.milanoteValues?.isCurrentlyResizing) return;

        throttledSyncColWidths();
        repositionAutofillHandle(hotTableInstanceRef.current, hotTableContainerRef);
    }, [tableContentColWidthsGU, gridSize]);
};

export default useTableStateSyncEffects;
